import React, { useState, useEffect } from "react";
import api, { apiErr } from "@/lib/api";
import { Mail, MessageSquare, CheckCircle2, AlertTriangle, Play, RefreshCw, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function MessagingPanel() {
  const [health, setHealth] = useState(null);
  const [log, setLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [testEmail, setTestEmail] = useState("");
  const [testPhone, setTestPhone] = useState("");
  const [busyE, setBusyE] = useState(false);
  const [busyS, setBusyS] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const [h, l] = await Promise.all([
        api.get("/admin/messaging/health"),
        api.get("/admin/messaging/log", { params: { limit: 20 } }),
      ]);
      setHealth(h.data); setLog(l.data.items || []);
    } catch (e) { toast.error(apiErr(e, "Errore health Brevo")); }
    finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, []);

  const doTest = async (kind) => {
    const setBusy = kind === "email" ? setBusyE : setBusyS;
    const path = kind === "email" ? "test-email" : "test-sms";
    const payload = kind === "email" ? { to: testEmail } : { to: testPhone };
    if (!payload.to) { toast.error("Indica un destinatario"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/messaging/${path}`, payload);
      if (data.ok) toast.success(`${kind.toUpperCase()} inviato — messageId: ${data.messageId || "—"}`);
      else toast.error(`${kind.toUpperCase()} fallito: ${data.error || data.status || "errore Brevo"}`);
      await refresh();
    } catch (e) { toast.error(apiErr(e, `Errore test ${kind}`)); }
    finally { setBusy(false); }
  };

  const h = health || {};
  const ok = h.api_key_valid;

  return (
    <div className="space-y-4" data-testid="admin-messaging-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Mail size={20} className="text-brand-azure" />
          <h2 className="font-extrabold text-brand-navy text-xl">Messaggi · Brevo</h2>
        </div>
        <button onClick={refresh} disabled={loading} className="inline-flex items-center gap-1.5 text-xs font-bold text-slate-600 hover:text-brand-navy px-3 py-2 rounded-lg border border-[#E2E8F0]" data-testid="messaging-refresh">
          {loading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />} Aggiorna
        </button>
      </div>

      <div className={`rounded-xl p-4 border ${ok ? "bg-emerald-50 border-emerald-300" : "bg-red-50 border-red-300"}`} data-testid="messaging-health-card">
        <div className="flex items-center gap-2 mb-2">
          {ok ? <CheckCircle2 size={16} className="text-emerald-700" /> : <AlertTriangle size={16} className="text-red-700" />}
          <span className={`font-bold ${ok ? "text-emerald-800" : "text-red-800"}`}>
            {ok ? "Brevo configurato e API key valida" : "Brevo NON funzionante"}
          </span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-xs">
          <div><b>API key:</b> {h.api_key_present ? "presente" : "mancante"}</div>
          <div><b>API valida:</b> {h.api_key_valid ? "sì" : "no"}</div>
          <div><b>HTTP:</b> {h.http_status || "—"}</div>
          <div><b>Sender email:</b> {h.sender_email_masked || "—"}</div>
          <div><b>Sender SMS:</b> {h.sms_sender || "—"}</div>
          <div><b>Plan:</b> {h.plan || "—"}</div>
          {h.account_email && <div className="col-span-2 md:col-span-3"><b>Account Brevo:</b> {h.account_email}</div>}
          {h.error && <div className="col-span-full text-red-700 font-bold">⚠ {h.error}</div>}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="bg-white border border-[#E2E8F0] rounded-xl p-4">
          <div className="text-xs uppercase tracking-wider text-slate-500 font-bold mb-2 flex items-center gap-1"><Mail size={12} /> Test Email</div>
          <input type="email" value={testEmail} onChange={(e) => setTestEmail(e.target.value)} placeholder="destinatario@email.it" data-testid="messaging-test-email-input" className="w-full px-3 py-2 rounded border border-[#E2E8F0] text-sm focus:outline-none focus:border-brand-azure" />
          <button onClick={() => doTest("email")} disabled={busyE || !ok} className="mt-2 inline-flex items-center gap-1.5 bg-brand-azure hover:bg-brand-navyDark text-white font-bold text-xs px-3 py-2 rounded-lg disabled:opacity-50" data-testid="messaging-test-email-send">
            {busyE ? <Loader2 size={12} className="animate-spin" /> : <Play size={12} />} Invia test email
          </button>
        </div>
        <div className="bg-white border border-[#E2E8F0] rounded-xl p-4">
          <div className="text-xs uppercase tracking-wider text-slate-500 font-bold mb-2 flex items-center gap-1"><MessageSquare size={12} /> Test SMS</div>
          <input type="tel" value={testPhone} onChange={(e) => setTestPhone(e.target.value)} placeholder="+39…" data-testid="messaging-test-sms-input" className="w-full px-3 py-2 rounded border border-[#E2E8F0] text-sm focus:outline-none focus:border-brand-azure" />
          <button onClick={() => doTest("sms")} disabled={busyS || !ok} className="mt-2 inline-flex items-center gap-1.5 bg-brand-azure hover:bg-brand-navyDark text-white font-bold text-xs px-3 py-2 rounded-lg disabled:opacity-50" data-testid="messaging-test-sms-send">
            {busyS ? <Loader2 size={12} className="animate-spin" /> : <Play size={12} />} Invia test SMS
          </button>
        </div>
      </div>

      <div className="bg-white border border-[#E2E8F0] rounded-xl p-4">
        <h3 className="text-xs uppercase tracking-wider text-slate-500 font-bold mb-2">Log invii recenti ({log.length})</h3>
        {log.length === 0 ? (
          <div className="text-sm text-slate-500 italic">Nessun invio registrato.</div>
        ) : (
          <table className="w-full text-xs" data-testid="messaging-log-table">
            <thead className="text-slate-500"><tr className="border-b border-slate-100"><th className="text-left py-1.5 font-bold">Data</th><th className="text-left font-bold">Canale</th><th className="text-left font-bold">Destinatario</th><th className="text-left font-bold">Esito</th><th className="text-left font-bold">MsgID / Error</th></tr></thead>
            <tbody>
              {log.map((it, i) => (
                <tr key={i} className="border-b border-slate-50">
                  <td className="py-1">{new Date(it.created_at).toLocaleString("it-IT")}</td>
                  <td>{it.channel}</td>
                  <td className="font-mono">{it.to_mask}</td>
                  <td className={it.ok ? "text-emerald-700 font-bold" : "text-red-700 font-bold"}>{it.ok ? "OK" : "FAIL"}</td>
                  <td className="font-mono text-[10px]">{it.messageId || it.error || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
